import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getStripe } from '@/lib/stripe';
import { Check, Play, Clock, Shield, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import MuseVideoPlayer from '@/components/MuseVideoPlayer';

const VideoPlaceholder = () => (
  <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
    <div className="aspect-video flex items-center justify-center">
      <Play className="h-16 w-16 text-white opacity-80" />
    </div>
  </div>
);

const FeatureList = ({ features }: { features: string[] }) => (
  <ul className="space-y-2">
    {features.map((feature, index) => (
      <li key={index} className="flex items-center gap-3">
        <Check className="h-5 w-5 text-book-yellow flex-shrink-0" />
        <span className="text-book-yellow">{feature}</span>
      </li>
    ))}
  </ul>
);

const Downsell = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isDeclining, setIsDeclining] = useState<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('checkout_session_id');
    if (id) {
      setSessionId(id);
    }
  }, []);

  const isActionDisabled = !sessionId;

  const CREATE_CHECKOUT_URL = 'https://guide.workstreamer.com/api/v1/liberated-consultant/create-checkout-session'

  function getDownsellPriceId(): string {
    return (
      ((import.meta as any).env?.VITE_WORKSTREAMER_PRO_PRICE_ID as string | undefined) ??
      ((import.meta as any).env?.WORKSTREAMER_PRO_PRICE_ID as string | undefined) ??
      ''
    )
  }

  async function createDownsellCheckoutSession(existingCheckoutSessionId: string): Promise<{ url?: string; sessionId?: string } | null> {
    const priceId = getDownsellPriceId()
    if (!priceId) {
      console.error('Missing price id (VITE_WORKSTREAMER_PRO_PRICE_ID)')
      return null
    }
    if (!existingCheckoutSessionId) {
      console.error('Missing required checkout_session_id for downsell checkout')
      return null
    }
    try {
      const successPath = `downsell-success.html`
      const cancelPath = `downsell.html`
      const res = await fetch(CREATE_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ price_id: priceId, checkout_session_id: existingCheckoutSessionId, success_path: successPath, cancel_path: cancelPath, metadata: { downsell_accepted: true } }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Downsell checkout session request failed', { status: res.status, statusText: res.statusText, body: text })
        return null
      }
      const json = await res.json().catch(() => null)
      if (!json) {
        console.error('Failed to parse downsell checkout session response')
        return null
      }
      const result = { url: json?.url, sessionId: json?.sessionId ?? json?.id }
      if (!result.url && !result.sessionId) {
        console.error('No url or sessionId returned from downsell checkout session response')
        return null
      }
      return result
    } catch (error) {
      console.error('Failed to create downsell checkout session', error)
      return null
    }
  }

  async function redirectToCheckout(result: { url?: string; sessionId?: string } | null) {
    if (!result) return
    if (result.url && typeof result.url === 'string') {
      window.location.href = result.url
      return
    }
    if (result.sessionId && typeof result.sessionId === 'string') {
      const stripe = await getStripe()
      await stripe?.redirectToCheckout({ sessionId: result.sessionId })
      return
    }
    console.error('No downsell checkout session url or id returned')
  }

  const handleAcceptOffer = async () => {
    if (!sessionId) {
      console.error('Missing checkout_session_id in URL; cannot proceed with downsell checkout.')
      return
    }
    try {
      setIsAccepting(true)
      const session = await createDownsellCheckoutSession(sessionId)
      await redirectToCheckout(session)
    } finally {
      setIsAccepting(false)
    }
  };

  const handleDeclineOffer = async () => {
    if (!sessionId) {
      console.error('Missing checkout_session_id in URL; cannot navigate to downsell-decline.')
      return
    }
    const destination = `/downsell-decline.html?checkout_session_id=${encodeURIComponent(sessionId)}`
    try {
      setIsDeclining(true)
      const res = await fetch('https://guide.workstreamer.com/api/v1/liberated-consultant/downsell-rejected', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ checkout_session_id: sessionId }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Downsell rejection tracking request failed', { status: res.status, statusText: res.statusText, body: text })
      }
    } catch (error) {
      console.error('Failed to notify downsell rejection', error)
    } finally {
      window.location.href = destination;
    }
  };

  const projectFeatures = [
    "Requirement tracking and management",
    "Built-in Client Sign off of Requirements/User Stories",
    "Capture notes and open items",
    "Collaborate with clients and team members or use it solo"
  ];

  const guideFeatures = [
    "Professional documentation in 30 minutes vs. 4 hours",
    "Automated screenshot capture on every click",
    "Training materials clients actually understand and follow"
  ];

  const disabledTooltip = 'Please complete your book order first to unlock this offer.'

  return (
    <>
      <TooltipProvider>
      <Helmet>
        <title>WorkStreamer Project + Guide - Final Offer | The Liberated Consultant</title>
        <meta name="description" content="Reclaim time and revenue with WorkStreamer Project & Guide for less than 1 billable hour - just $97 for the next 12 months." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-book-lightBg">
        {/* Special Offer Bar */}
        <div className="bg-gradient-to-r from-book-yellow to-book-yellow text-book-darkBlue py-3 px-4 text-center">
          <p className="text-sm md:text-base font-semibold">
            🔓 Special offer unlocked - Please select an option on this page to complete your order.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-gray-100 py-4 px-4">
          <div className="container mx-auto">
            <div className="flex items-center justify-center space-x-2 md:space-x-4 text-sm">
              <span className="text-gray-500">STEP 1: OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="font-bold text-book-yellowDark">STEP 2: SPECIAL OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-500">STEP 3: ACCESS</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <section className="bg-white py-6 md:py-10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight max-w-6xl mx-auto">
              Reclaim Time and Revenue: 1 Year of Productivity Tools for Less Than 1 Billable Hour
            </h1>
            
            <p className="text-lg text-black mb-8 font-semibold max-w-4xl mx-auto">
              Watch this short video to see how you can streamline documentation as an independent consultant
            </p>

            {/* Video Section */}
            <div className="mb-8 max-w-4xl mx-auto">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                <div className="aspect-video">
                  <MuseVideoPlayer videoId="3AJL9UP" className="w-full h-full" />
                </div>
              </div>
              <div className="mt-8 text-center">

                      <p className="text-base text-gray-700 mb-8 max-w-4xl mx-auto">
              Clicking the button will automatically subscribe you to WorkStreamer Guide & Project for $97/year.
            </p>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                      <Button 
                        onClick={handleAcceptOffer}
                        className="bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 px-4 sm:px-8 text-sm sm:text-lg shadow-lg transition-all duration-300 rounded-md mb-4 w-full sm:w-auto text-center"
                        size="lg"
                        disabled={isActionDisabled || isAccepting}
                      >
                        {isAccepting ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          <span className="block sm:hidden">
                            <span className="block">Yes, Upgrade My Order</span>
                            <span className="block">to Add 2 WorkStreamer Accelerators</span>
                          </span>
                        )}
                        {!isAccepting && (
                          <span className="hidden sm:block">
                            Yes, Upgrade My Order to Add 2 WorkStreamer Accelerators
                          </span>
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
                </Tooltip>
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                        <button 
                          onClick={handleDeclineOffer}
                          className="text-gray-600 hover:text-gray-800 underline text-sm transition-colors"
                          disabled={isActionDisabled || isDeclining || isAccepting}
                        >
                          {isDeclining ? (
                            <span className="inline-flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Redirecting...
                            </span>
                          ) : (
                            "No thanks, I don't want this special offer"
                          )}
                        </button>
                      </span>
                    </TooltipTrigger>
                    {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section */}
        <section className="light-section">
          <div className="container mx-auto px-4">
            <h2 className="section-title text-book-red">
              The Two Problems Stealing Your Freedom
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                While you're implementing cutting-edge solutions for clients, you're hitting friction with low-value documentation tasks:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-lg border border-book-red/30">
                  <h3 className="font-bold text-book-darkBlue mb-4 text-xl">Problem #1: Design Documentation Chaos</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Constant updates to requirement and user stories</li>
                    <li>• Endless scope creep, no design sign-off</li>
                    <li>• Back and forth with updated documents</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg border border-book-red/30">
                  <h3 className="font-bold text-book-darkBlue mb-4 text-xl">Problem #2: Screenshot Documentation Overwhelm</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Hours creating training that clients don't use</li>
                    <li>• Never ending screenshots for admin user guides</li>
                    <li>• Excessive client meetings to review processes again</li>
                  </ul>
                </div>
              </div>
              
              <div className="text-center bg-book-red/10 p-6 rounded-lg border border-book-red/30">
                <p className="text-xl font-bold text-book-red">
                  At $100/hr billing rate, these two problems alone cost you thousands of dollars each month that could be spent taking on more work or relaxing and enjoying life.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="dark-section">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-6 text-white">
              WorkStreamer Project + Guide: Your Strategic Solution
            </h2>
            <p className="text-xl text-white/90 text-center mb-16 max-w-4xl mx-auto leading-relaxed">
              Instead of the full 3-accelerator suite, here are the two tools that deliver immediate impact:
            </p>

            {/* Project Accelerator */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-book-yellow mb-8">WorkStreamer Project Accelerator</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <FeatureList features={projectFeatures} />
                </div>
                <div>
                  <div className="relative rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                    <div className="aspect-video">
                      <MuseVideoPlayer videoId="aeZSZid" className="w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Guide Accelerator */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-book-yellow mb-8">WorkStreamer Guide Accelerator</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <FeatureList features={guideFeatures} />
                </div>
                <div>
                  <div className="relative rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
                    <div className="aspect-video">
                      <MuseVideoPlayer videoId="NjoNs2Z" className="w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center bg-book-yellow/20 p-6 rounded-lg border border-book-yellow/30 mb-8">
              <p className="text-2xl font-bold text-book-yellow">
                The Mathematics of Freedom: Save just 10 hours monthly = $1,000 additional billable time. Every single month.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="gradient-section">
          <div className="container mx-auto px-4 text-center">
            <h2 className="section-title">Your Strategic Advantage</h2>
            
            <div className="max-w-2xl mx-auto mb-12">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-book-yellow/30">
                <div className="mb-6">
                  <div className="text-lg text-gray-600 mb-2">Regular Investment:</div>
                  <div className="text-3xl font-bold text-gray-400 line-through">$500/year for both accelerators</div>
                </div>
                <div className="mb-6">
                  <div className="text-lg text-book-red mb-2">Today Only:</div>
                  <div className="text-5xl font-bold text-book-red">$97</div>
                  <div className="text-lg text-book-darkBlue">per year</div>
                </div>
                <div className="text-lg text-book-darkBlue font-semibold">
                  That's 80% off—less than you spend on coffee each month—to reclaim 10+ hours monthly.
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-book-darkBlue mb-6">Why This Unprecedented Discount?</h3>
            <p className="text-lg text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed">
              You've taken action by purchasing The Liberated Consultant book. I want to connect with action takers that invest in themselves. 
              I want to reward that and bring you into my network. Part of that means getting you setup with WorkStreamer at a big discount.
            </p>

          </div>
        </section>

        {/* Guarantee Section */}
        <section className="light-section">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Shield className="h-16 w-16 text-book-yellow mr-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-book-darkBlue">Iron-Clad, 365-Day Money-Back Promise</h2>
              </div>
              
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Put WorkStreamer to work. If you don't think it's worth ten times what it costs, just reach out. 
                My team will refund every penny. No questions asked.
              </p>
            </div>
          </div>
        </section>

        {/* Urgency Section */}
        <section className="dark-section">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-book-yellow mb-6">The Window Closes</h2>
            
            <p className="text-lg text-white/90 mb-8 max-w-4xl mx-auto leading-relaxed">
              At this low price, I can't afford to offer this to everyone long term. It's just too much work for myself and my team to manage and onboard everyone.
            </p>
            
            <p className="text-xl font-bold text-book-yellow mb-12">
              Once you leave this page the regular $500 pricing returns. Don't regret missing this strategic opportunity.
            </p>

            <div className="mb-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <Button 
                      onClick={handleAcceptOffer}
                      className="bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 px-4 sm:px-8 text-sm sm:text-lg shadow-lg transition-all duration-300 rounded-md animate-subtle-pulse w-full sm:w-auto text-center"
                      size="lg"
                      disabled={isActionDisabled || isAccepting}
                    >
                      {isAccepting ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="block sm:hidden">
                          <span className="block">Yes, Upgrade My Order</span>
                          <span className="block">to Add WorkStreamer Project & Guide for $97/year</span>
                        </span>
                      )}
                      {!isAccepting && (
                        <span className="hidden sm:block">
                          Yes, Upgrade My Order to Add WorkStreamer Project & Guide for $97/year
                        </span>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
            </div>
            <div className="mb-8">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                    <button 
                      onClick={handleDeclineOffer}
                      className="text-white/80 hover:text-white underline text-sm transition-colors"
                      disabled={isActionDisabled || isDeclining || isAccepting}
                    >
                      {isDeclining ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Redirecting...
                        </span>
                      ) : (
                        "No thanks, I don't want this special offer"
                      )}
                    </button>
                  </span>
                </TooltipTrigger>
                {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
              </Tooltip>
            </div>
          </div>
        </section>

        {/* Final Section */}
        <section className="gradient-section">
          <div className="container mx-auto px-4">
            <h2 className="section-title">Smart Consultants Act Decisively</h2>
            
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                While others struggle formatting their deliverables with Excel and Word, you'll be pumping out high-quality and professional 
                deliverables in record speed. You'll be that amazing contractor that comes in and impresses everyone on the project. 
                You'll get good feedback from clients, offered jobs at the consulting company, and be invited back for more projects. 
                I know because it's happened to me.
              </p>
              
              <div className="text-center bg-book-yellow/20 p-8 rounded-lg border border-book-yellow/30 mb-8">
                <p className="text-2xl font-bold text-book-darkBlue mb-4">
                  Two tools. Two problems solved. At the cost of one billable hour.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-book-darkBlue mb-6">Final Thought</h3>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Speed to implementation beats perfection. These two accelerators eliminate your biggest time-wasters immediately. 
                Plus, it provides a solid foundation when you contract through small and mid-sized consulting companies that don't have 
                the technology or resources to provide you with productivity tools.  You'll have to figure it out.  WorkStreamer solves that challenge.
              </p>
              
              <p className="text-xl font-bold text-book-red mb-8">
                Less than $100 to reclaim your freedom and double your productivity. One year money back guarantee.
              </p>
              
              <p className="text-lg text-gray-700 mb-12">
                Start your independent consulting career with the right tools, designed by consultants for consultants.
              </p>

              <div className="text-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                      <Button 
                        onClick={handleAcceptOffer}
                        className="bg-book-red hover:bg-book-red/90 text-white font-bold py-4 sm:py-6 px-4 sm:px-12 text-sm sm:text-xl shadow-lg transition-all duration-300 rounded-md mb-4 w-full sm:w-auto text-center"
                        size="lg"
                        disabled={isActionDisabled || isAccepting}
                      >
                        {isAccepting ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          <span className="block sm:hidden">
                            <span className="block">Yes, Upgrade My Order</span>
                            <span className="block">to Add WorkStreamer Project & Guide for $97/year</span>
                          </span>
                        )}
                        {!isAccepting && (
                          <span className="hidden sm:block">
                            Yes, Upgrade My Order to Add WorkStreamer Project & Guide for $97/year
                          </span>
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
                </Tooltip>
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block" tabIndex={isActionDisabled ? 0 : -1}>
                        <button 
                          onClick={handleDeclineOffer}
                          className="text-gray-600 hover:text-gray-800 underline text-sm transition-colors"
                          disabled={isActionDisabled || isDeclining || isAccepting}
                        >
                          {isDeclining ? (
                            <span className="inline-flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Redirecting...
                            </span>
                          ) : (
                            "No thanks, I don't want this special offer"
                          )}
                        </button>
                      </span>
                    </TooltipTrigger>
                    {isActionDisabled && <TooltipContent>{disabledTooltip}</TooltipContent>}
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      </TooltipProvider>
    </>
  );
};

export default Downsell;